'use client';

import React, { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMenuOpen(false);
    if (pathname === '/') {
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      router.push(`/${href}`);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled ? 'nav-blur py-3 shadow-sm shadow-black/5' : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center gap-3 group"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            <div className="flex items-center rounded-sm">
              <img
                alt="Logo"
                width={40}
                height={40}
                src="https://buchhaltungaktas-4lh9a97.public.builtwithrocket.new/_next/image?url=%2Fassets%2Fimages%2FAktas_3-1785851522351.png&w=96&q=85"
                className="flex-shrink-0"
                style={{ color: 'transparent' }}
              />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-semibold text-sm tracking-wide" style={{ color: 'var(--foreground)' }}>Sulleyman Aktas</span>
              <span className="text-xs font-medium tracking-wider uppercase" style={{ color: 'var(--muted-foreground)' }}>Buchhaltungsbüro</span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#leistungen"
              onClick={(e) => handleNavClick(e, '#leistungen')}
              className="text-sm font-medium transition-colors gold-underline"
              style={{ color: 'var(--muted-foreground)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--foreground)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted-foreground)')}
            >
              Leistungen
            </a>
            <a
              href="#ueber-uns"
              onClick={(e) => handleNavClick(e, '#ueber-uns')}
              className="text-sm font-medium transition-colors gold-underline"
              style={{ color: 'var(--muted-foreground)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--foreground)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted-foreground)')}
            >
              Über uns
            </a>
            <a
              href="#kontakt"
              onClick={(e) => handleNavClick(e, '#kontakt')}
              className="text-sm font-medium transition-colors gold-underline"
              style={{ color: 'var(--muted-foreground)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--foreground)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted-foreground)')}
            >
              Kontakt
            </a>
            <a
              href="#kontakt"
              onClick={(e) => handleNavClick(e, '#kontakt')}
              className="px-5 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 hover:scale-[1.03]"
              style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--primary)')}
            >
              Beratung anfragen
            </a>
            <a
              href="https://www.instagram.com/buchhaltungsbuero.aktas/?next=%2F"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-9 h-9 flex items-center justify-center rounded-full border transition-colors hover:scale-[1.08]"
              style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#E1306C'; e.currentTarget.style.borderColor = '#E1306C'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--muted-foreground)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden w-11 h-11 flex flex-col items-center justify-center gap-1.5 rounded-full border transition-colors"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menü öffnen"
          >
            <span
              className={`block w-5 h-0.5 transition-all duration-300 ${
                menuOpen ? 'rotate-45 translate-y-2' : ''
              }`}
              style={{ backgroundColor: 'var(--foreground)' }}
            />
            <span
              className={`block w-5 h-0.5 transition-all duration-300 ${
                menuOpen ? 'opacity-0' : ''
              }`}
              style={{ backgroundColor: 'var(--foreground)' }}
            />
            <span
              className={`block w-5 h-0.5 transition-all duration-300 ${
                menuOpen ? '-rotate-45 -translate-y-2' : ''
              }`}
              style={{ backgroundColor: 'var(--foreground)' }}
            />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-400 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 backdrop-blur-xl" style={{ backgroundColor: 'rgba(247,245,242,0.95)' }} />
        <nav className="absolute inset-0 flex flex-col items-center justify-center gap-6">
          {['#leistungen', '#ueber-uns', '#kontakt'].map((href, i) => (
            <a
              key={href}
              href={href}
              onClick={(e) => handleNavClick(e, href)}
              className="font-display text-3xl font-semibold transition-colors py-2 min-h-[56px] flex items-center"
              style={{ color: 'var(--foreground)', transitionDelay: `${i * 60}ms` }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--foreground)')}
            >
              {href === '#leistungen' ? 'Leistungen' : href === '#ueber-uns' ? 'Über uns' : 'Kontakt'}
            </a>
          ))}
          <a
            href="#kontakt"
            onClick={(e) => handleNavClick(e, '#kontakt')}
            className="mt-4 px-8 py-4 text-base font-semibold rounded-full transition-all duration-300 min-h-[52px] flex items-center"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            Beratung anfragen
          </a>
          <a
            href="https://www.instagram.com/buchhaltungsbuero.aktas/?next=%2F"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="mt-2 flex items-center gap-2 text-base font-medium min-h-[52px] px-4 py-3 rounded-full border transition-colors"
            style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
            </svg>
            Instagram
          </a>
        </nav>
      </div>
    </>
  );
}
